import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Refresh } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { StreamingTranscriber } from 'assemblyai';

import { useRealtimeTranscription } from '../hooks/useRealtimeTranscription';

interface VoiceInstructionInputProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
  hidden?: boolean;
  onRecordingChange?: (isRecording: boolean) => void;
  externalToggle?: boolean;
}

/**
 * Voice instruction input component using AssemblyAI realtime transcription
 *
 * Features:
 * - Realtime speech-to-text via AssemblyAI WebSocket
 * - Visual recording indicator
 * - Error handling and recovery
 * - Token management
 */
export const VoiceInstructionInput: React.FC<VoiceInstructionInputProps> = ({
  onTranscript,
  disabled = false,
  hidden = false,
  onRecordingChange,
  externalToggle,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const prevExternalToggleRef = useRef(externalToggle);

  // Notify parent when recording state changes
  useEffect(() => {
    onRecordingChange?.(isRecording);
  }, [isRecording, onRecordingChange]);

  // Handle external toggle changes
  useEffect(() => {
    if (
      hidden &&
      externalToggle !== undefined &&
      externalToggle !== prevExternalToggleRef.current
    ) {
      if (externalToggle && !isRecording) {
        startRecording();
      } else if (!externalToggle && isRecording) {
        stopRecording();
      }
      prevExternalToggleRef.current = externalToggle;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalToggle]);

  const {
    token,
    isLoading: isLoadingToken,
    error: tokenError,
    refreshToken,
  } = useRealtimeTranscription({ fetchOnMount: false }); // Lazy load - only fetch when recording starts

  const transcriberRef = useRef<StreamingTranscriber | null>(null);
  const audioResourcesRef = useRef<{
    processor: ScriptProcessorNode;
    audioContext: AudioContext;
    stream: MediaStream;
  } | null>(null);
  const pendingRecordingRef = useRef(false);

  // Start recording with a given token
  const startRecordingWithToken = useCallback(
    async (sessionToken: string) => {
      if (isRecording) return;

      try {
        setRecordingError(null);
        setTranscript('');

        // Request microphone permission with specific constraints
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1, // Mono audio required
            sampleRate: 16000, // Match transcriber sample rate
            echoCancellation: true,
            noiseSuppression: true,
          },
        });

        // Create AssemblyAI streaming transcriber with the passed token
        const transcriber = new StreamingTranscriber({
          token: sessionToken,
          sampleRate: 16_000,
          encoding: 'pcm_s16le', // PCM16 encoding as per docs
          speechModel: 'universal-streaming-multilingual',
        });

        // Handle transcription events
        transcriber.on('turn', event => {
          // Only process when we have end of turn (complete sentence)
          if (event.end_of_turn && event.transcript.trim()) {
            const newText = event.transcript.trim();
            setTranscript(prev => (prev ? `${prev} ${newText}` : newText));
            // Send to parent immediately
            onTranscript(newText);
          }
        });

        transcriber.on('error', (error: Error) => {
          console.error('Transcription error:', error);
          setRecordingError('Transcription error occurred. Please try again.');
          // Clean up recording on error
          if (audioResourcesRef.current) {
            const { processor, audioContext, stream } = audioResourcesRef.current;
            if (processor) processor.disconnect();
            if (audioContext) audioContext.close();
            if (stream) stream.getTracks().forEach(track => track.stop());
            audioResourcesRef.current = null;
          }
          if (transcriberRef.current) {
            transcriberRef.current.close();
            transcriberRef.current = null;
          }
          setIsRecording(false);
          pendingRecordingRef.current = false;
        });

        transcriber.on('close', (code: number, reason: string) => {
          console.debug('Transcriber closed:', code, reason);
          if (code !== 1000) {
            // Not a normal closure
            setRecordingError('Connection closed unexpectedly.');
          }
        });

        // Connect to AssemblyAI
        await transcriber.connect();

        // Use Web Audio API to get PCM samples
        const audioContext = new AudioContext({ sampleRate: 16000 });
        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);

        source.connect(processor);
        processor.connect(audioContext.destination);

        processor.onaudioprocess = e => {
          if (!transcriberRef.current) return;

          try {
            // Get PCM samples as Float32Array
            const inputData = e.inputBuffer.getChannelData(0);

            // Convert Float32 (-1.0 to 1.0) to Int16 PCM (-32768 to 32767)
            const pcm16 = new Int16Array(inputData.length);
            for (let i = 0; i < inputData.length; i++) {
              const s = Math.max(-1, Math.min(1, inputData[i]));
              pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
            }

            // Send PCM16 data to AssemblyAI
            transcriberRef.current.sendAudio(pcm16.buffer);
          } catch (err) {
            console.error('Error sending audio data:', err);
          }
        };

        transcriberRef.current = transcriber;
        audioResourcesRef.current = { processor, audioContext, stream };
        setIsRecording(true);
        pendingRecordingRef.current = false;
      } catch (err) {
        console.error('Error starting recording:', err);
        pendingRecordingRef.current = false;

        const error = err as Error;
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setRecordingError('Microphone access denied. Please enable microphone permissions.');
        } else if (error.name === 'NotFoundError') {
          setRecordingError('No microphone found. Please connect a microphone.');
        } else {
          setRecordingError('Failed to start recording. Please try again.');
        }
      }
    },
    [isRecording, onTranscript],
  );

  // Start recording - fetches token lazily if needed
  const startRecording = useCallback(async () => {
    if (isRecording || pendingRecordingRef.current) return;

    // If we already have a valid token, use it
    if (token) {
      await startRecordingWithToken(token);
      return;
    }

    // Otherwise, request a new token first
    pendingRecordingRef.current = true;
    await refreshToken();
    // The token will be available after refreshToken completes
    // We'll start recording in the useEffect that watches for token changes
  }, [isRecording, token, startRecordingWithToken, refreshToken]);

  // When token becomes available and we have a pending recording request, start recording
  useEffect(() => {
    if (token && pendingRecordingRef.current && !isRecording) {
      startRecordingWithToken(token);
    }
  }, [token, isRecording, startRecordingWithToken]);

  const stopRecording = useCallback(() => {
    // Clean up Web Audio API resources
    if (audioResourcesRef.current) {
      const { processor, audioContext, stream } = audioResourcesRef.current;

      if (processor) {
        processor.disconnect();
      }

      if (audioContext) {
        audioContext.close();
      }

      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      audioResourcesRef.current = null;
    }

    // Close transcriber
    if (transcriberRef.current) {
      transcriberRef.current.close();
      transcriberRef.current = null;
    }

    setIsRecording(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [stopRecording]);

  const handleToggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleRefreshToken = async () => {
    setRecordingError(null);
    await refreshToken();
  };

  // Only disable when explicitly disabled or while loading a token (after user clicked)
  const isDisabled = disabled || isLoadingToken;

  // If hidden, only show errors
  if (hidden) {
    return (
      <Box sx={{ display: 'none' }}>
        {/* Only show errors if they occur */}
        {tokenError && (
          <Alert
            severity="error"
            action={
              <IconButton size="small" onClick={handleRefreshToken}>
                <Refresh />
              </IconButton>
            }
          >
            {tokenError}
          </Alert>
        )}
        {recordingError && (
          <Alert severity="warning" onClose={() => setRecordingError(null)}>
            {recordingError}
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Token/Error State */}
      {tokenError && (
        <Alert
          severity="error"
          action={
            <IconButton size="small" onClick={handleRefreshToken}>
              <Refresh />
            </IconButton>
          }
        >
          {tokenError}
        </Alert>
      )}

      {recordingError && (
        <Alert severity="warning" onClose={() => setRecordingError(null)}>
          {recordingError}
        </Alert>
      )}

      {/* Recording Controls */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          p: 3,
          border: '2px dashed',
          borderColor: isRecording ? 'error.main' : 'divider',
          borderRadius: 2,
          bgcolor: isRecording ? 'error.lighter' : 'background.paper',
          transition: 'all 0.3s ease',
        }}
      >
        {/* Recording Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isRecording ? (
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: 'error.main',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                },
              }}
            />
          ) : null}
          <Typography variant="body2" color="text.secondary">
            {isRecording ? 'Recording...' : 'Click to start recording'}
          </Typography>
        </Box>

        {/* Microphone Button */}
        <Button
          variant={isRecording ? 'contained' : 'outlined'}
          color={isRecording ? 'error' : 'primary'}
          size="large"
          onClick={handleToggleRecording}
          disabled={isDisabled}
          startIcon={
            isLoadingToken ? <CircularProgress size={20} /> : isRecording ? <MicOff /> : <Mic />
          }
          sx={{
            minWidth: 200,
            py: 1.5,
          }}
        >
          {isLoadingToken ? 'Loading...' : isRecording ? 'Stop Recording' : 'Start Recording'}
        </Button>

        {/* Transcript Preview */}
        {transcript && (
          <Box
            sx={{
              width: '100%',
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Transcript:
            </Typography>
            <Typography variant="body2">{transcript}</Typography>
          </Box>
        )}
      </Box>

      {/* Help Text */}
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
        Speak clearly and pause after each instruction. Your speech will be converted to text
        automatically.
      </Typography>
    </Box>
  );
};
